import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Mail, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import RichTextComposer from "./RichTextComposer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIABLES = ["firma", "ansprechpartner", "anrede", "stadt", "vertriebler", "telefon", "website"];

const empty = { name: "", subject: "", body_html: "", description: "" };

export default function EmailTemplatesSettings() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | template-id
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const r = await axios.get(`${API}/email-templates`, { withCredentials: true });
    setTemplates(r.data);
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (t) => { setEditing(t.id); setForm({ name: t.name, subject: t.subject, body_html: t.body_html || "", description: t.description || "" }); };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.subject) { toast.error("Name + Betreff sind Pflicht"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/email-templates`, form, { withCredentials: true });
        toast.success("Template angelegt");
      } else {
        await axios.put(`${API}/email-templates/${editing}`, form, { withCredentials: true });
        toast.success("Template gespeichert");
      }
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Template wirklich löschen?")) return;
    await axios.delete(`${API}/email-templates/${id}`, { withCredentials: true });
    reload();
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="email-templates">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Sales · Outreach</p>
          <h2 className="font-heading text-xl font-black tracking-tight">E-Mail-Vorlagen</h2>
          <p className="text-sm text-muted-foreground mt-1">Einmal anlegen, mit einem Klick personalisiert versenden.</p>
        </div>
        <Button onClick={startNew} className="rounded-sm gap-1.5" size="sm" data-testid="add-template-btn">
          <Plus size={13} /> Neues Template
        </Button>
      </div>

      <div className="space-y-2">
        {templates.length === 0 && <div className="border border-dashed border-border rounded-sm p-12 text-center">
          <Mail size={28} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Noch keine Templates</p>
        </div>}
        {templates.map(t => (
          <div key={t.id} className="bg-card border border-border rounded-sm p-3 flex items-center gap-3 group" data-testid={`tpl-${t.id}`}>
            <Mail size={14} className="text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{t.name}</p>
              <p className="text-xs text-muted-foreground truncate">{t.subject}</p>
              {t.description && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{t.description}</p>}
            </div>
            <button onClick={() => startEdit(t)} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground" data-testid={`edit-tpl-${t.id}`}>
              <Edit2 size={13} />
            </button>
            <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-destructive">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editing === "new" ? "Neues Template" : "Template bearbeiten"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="tpl-form">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name (intern) *</Label>
              <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z.B. Erstkontakt für Praxen" className="rounded-sm" data-testid="tpl-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Betreff *</Label>
              <Input required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Modernisierung Ihrer Website — {{firma}}" className="rounded-sm" data-testid="tpl-subject" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">Inhalt</Label>
              <RichTextComposer
                value={form.body_html}
                onChange={(v) => setForm(f => ({ ...f, body_html: v }))}
                variables={VARIABLES}
                placeholder="Hallo {{ansprechpartner}}, ich habe Ihre Praxis in {{stadt}} entdeckt …"
                testId="tpl-body-composer"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Beschreibung (intern)</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="rounded-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-sm gap-1.5"><X size={13} /> Abbrechen</Button>
              <Button type="submit" disabled={saving} className="rounded-sm gap-1.5" data-testid="tpl-save"><Save size={13} /> Speichern</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
